import type { Config, EventBase, IdentifyPayload } from './types'
import { getAnonId, getSessionId } from './ids'
import { safeGet, safeSet, CONSENT_KEY } from './storage'
import { postJSON, detectCountry } from './transport'

const isBrowser = typeof window !== 'undefined'
// Endpoint padrão definido em build (tsup define) ou fallback
const DEFAULT_ENDPOINT = (process as any)?.env?.ANALYTICS_DEFAULT_ENDPOINT || 'https://function-bun-production-3339.up.railway.app/v1/i'

type InternalConfig = Required<Omit<Config, 'endpoint'>> & { endpoint: string }

function pickUTM(): Record<string, string> {
  if (!isBrowser) return {}
  const sp = new URLSearchParams(window.location.search)
  const ret: Record<string, string> = {};
  ['utm_source','utm_medium','utm_campaign','utm_term','utm_content'].forEach(k=>{
    const v = sp.get(k); if (v) ret[k.replace('utm_','')] = v
  })
  return ret
}

function basePayload(): Partial<EventBase> {
  const lang = isBrowser ? (navigator.language || '') : ''
  const tz = isBrowser && 'Intl' in globalThis ? (Intl.DateTimeFormat().resolvedOptions().timeZone || '') : ''
  const viewport = isBrowser ? { w: window.innerWidth, h: window.innerHeight } : undefined
  const screenInfo = isBrowser ? { w: window.screen.width, h: window.screen.height } : undefined
  return { lang, tz, viewport, screen: screenInfo }
}

export class Analytics {
  private cfg: InternalConfig
  private q: EventBase[] = []
  private timer: number | null = null
  private isFlushing = false
  private anonId = ''
  private sessId = ''
  private userId: string | null = null
  private lastPageviewPath: string | null = null
  private autoPageviewInitialized = false
  private lastPageviewTs: number = 0
  private lastIdentifyTs: number = 0
  private lastIdentifyUserId: string | null = null

  constructor(cfg: Config) {
    if (!cfg.siteKey) throw new Error('siteKey is required')
    this.cfg = {
      siteKey: cfg.siteKey,
      endpoint: cfg.endpoint || DEFAULT_ENDPOINT,
      batchSize: cfg.batchSize ?? 10,
      flushIntervalMs: cfg.flushIntervalMs ?? 3000,
      respectDNT: cfg.respectDNT ?? true,
      autoPageview: cfg.autoPageview ?? true,
      debug: cfg.debug ?? false
    }
    this.anonId = getAnonId()
    this.sessId = getSessionId()

    if (this.cfg.autoPageview && isBrowser && !this.autoPageviewInitialized) {
      this.autoPageviewInitialized = true
      this.pageview()
      // flush on visibility change
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush()
      })
      window.addEventListener('beforeunload', () => this.flush())
    }
  }

  setConsent(status: 'granted' | 'denied') {
    safeSet(CONSENT_KEY, status)
  }

  identify(p: IdentifyPayload) {
    // Valida que id_prop foi fornecido
    if (!p.id_prop) {
      if (this.cfg.debug) {
        console.warn('[Analytics] identify: id_prop é obrigatório')
      }
      return
    }
    
    // Valida que a propriedade existe em traits
    if (!p.traits || !(p.id_prop in p.traits)) {
      if (this.cfg.debug) {
        console.warn(`[Analytics] identify: propriedade "${p.id_prop}" não encontrada em traits`)
      }
      return
    }
    
    // Throttle: não envia identify se for o mesmo user_id em menos de 5 minutos
    const now = Date.now()
    const FIVE_MIN = 5 * 60 * 1000
    if (
      this.lastIdentifyUserId === p.user_id &&
      this.lastIdentifyTs > 0 &&
      now - this.lastIdentifyTs < FIVE_MIN
    ) {
      if (this.cfg.debug) {
        console.log('[Analytics] identify: throttled (mesmo user_id em <5min)')
      }
      return
    }
    
    this.userId = p.user_id
    this.lastIdentifyUserId = p.user_id
    this.lastIdentifyTs = now
    
    // Inclui id_prop nos props para o backend persistir
    this.track('identify', { 
      user_id: p.user_id, 
      props: { 
        ...p.traits,
        id_prop: p.id_prop 
      } 
    })
  }

  pageview(props: Record<string, unknown> = {}) {
    if (!isBrowser) return
    
    const currentPath = window.location.pathname
    const now = Date.now()
    
    // Throttle: não envia pageview se for o mesmo path em menos de 2.5 segundos
    // (o backend também faz deduplicação, mas é melhor evitar enviar)
    if (
      this.lastPageviewPath === currentPath &&
      this.lastPageviewTs > 0 &&
      now - this.lastPageviewTs < 2500
    ) {
      if (this.cfg.debug) {
        console.log('[Analytics] pageview: throttled (mesmo path em <2.5s)')
      }
      return
    }
    
    // Verifica se já existe pageview na fila para o mesmo path
    const existingPageview = this.q.find(e => 
      e.event === 'page_view' && 
      e.path === currentPath &&
      e.ts && (now - e.ts) < 2500
    )
    if (existingPageview) {
      if (this.cfg.debug) {
        console.log('[Analytics] pageview: já existe na fila')
      }
      return
    }
    
    this.lastPageviewPath = currentPath
    this.lastPageviewTs = now
    
    const e: EventBase = {
      event: 'page_view',
      ts: now,
      url: window.location.href,
      path: currentPath,
      referrer: document.referrer || '',
      utm: pickUTM(),
      props
    }
    this.enqueue(e)
  }

  // Eventos de interação
  click(elementId?: string, elementText?: string, props: Record<string, unknown> = {}) {
    this.track('click', {
      element_id: elementId,
      element_text: elementText,
      ...props
    })
  }

  // Eventos de formulário
  formSubmit(formId?: string, formName?: string, props: Record<string, unknown> = {}) {
    this.track('form_submit', {
      form_id: formId,
      form_name: formName,
      ...props
    })
  }

  formStart(formId?: string, formName?: string, props: Record<string, unknown> = {}) {
    this.track('form_start', {
      form_id: formId,
      form_name: formName,
      ...props
    })
  }

  // Eventos de download/share
  download(fileName?: string, fileType?: string, props: Record<string, unknown> = {}) {
    this.track('download', {
      file_name: fileName,
      file_type: fileType,
      ...props
    })
  }

  share(method?: string, contentType?: string, itemId?: string, props: Record<string, unknown> = {}) {
    this.track('share', {
      method,
      content_type: contentType,
      item_id: itemId,
      ...props
    })
  }

  // Eventos de busca
  search(query?: string, category?: string, props: Record<string, unknown> = {}) {
    this.track('search', {
      query,
      category,
      ...props
    })
  }

  // Eventos de autenticação
  signup(method?: string, props: Record<string, unknown> = {}) {
    this.track('signup', {
      method,
      ...props
    })
  }

  login(method?: string, props: Record<string, unknown> = {}) {
    this.track('login', {
      method,
      ...props
    })
  }

  logout(props: Record<string, unknown> = {}) {
    this.track('logout', props)
  }

  // Eventos de e-commerce
  addToCart(productId?: string, productName?: string, price?: number, quantity?: number, props: Record<string, unknown> = {}) {
    this.track('add_to_cart', {
      product_id: productId,
      product_name: productName,
      price,
      quantity,
      ...props
    })
  }

  removeFromCart(productId?: string, productName?: string, props: Record<string, unknown> = {}) {
    this.track('remove_from_cart', {
      product_id: productId,
      product_name: productName,
      ...props
    })
  }

  viewItem(productId?: string, productName?: string, category?: string, price?: number, props: Record<string, unknown> = {}) {
    this.track('view_item', {
      product_id: productId,
      product_name: productName,
      category,
      price,
      ...props
    })
  }

  purchase(orderId?: string, value?: number, currency?: string, items?: Array<{ product_id?: string; product_name?: string; price?: number; quantity?: number }>, props: Record<string, unknown> = {}) {
    this.track('purchase', {
      order_id: orderId,
      value,
      currency: currency || 'USD',
      items,
      ...props
    })
  }

  beginCheckout(value?: number, currency?: string, props: Record<string, unknown> = {}) {
    this.track('begin_checkout', {
      value,
      currency: currency || 'USD',
      ...props
    })
  }

  // Eventos de vídeo
  videoPlay(videoId?: string, videoTitle?: string, duration?: number, props: Record<string, unknown> = {}) {
    this.track('video_play', {
      video_id: videoId,
      video_title: videoTitle,
      duration,
      ...props
    })
  }

  videoPause(videoId?: string, currentTime?: number, props: Record<string, unknown> = {}) {
    this.track('video_pause', {
      video_id: videoId,
      current_time: currentTime,
      ...props
    })
  }

  videoComplete(videoId?: string, duration?: number, props: Record<string, unknown> = {}) {
    this.track('video_complete', {
      video_id: videoId,
      duration,
      ...props
    })
  }

  // Eventos de navegação
  scroll(depth?: number, props: Record<string, unknown> = {}) {
    this.track('scroll', {
      depth,
      ...props
    })
  }

  // Método genérico para eventos customizados
  track(name: string, props: Record<string, unknown> = {}) {
    const url = isBrowser ? window.location.href : ''
    const path = isBrowser ? window.location.pathname : ''
    const e: EventBase = { event: name, ts: Date.now(), url, path, props }
    this.enqueue(e)
  }

  private enqueue(e: EventBase) {
    // DNT
    if (this.cfg.respectDNT && isBrowser && (navigator as any).doNotTrack === '1') return
    // Consent
    const consent = safeGet<'granted' | 'denied' | null>(CONSENT_KEY, null)
    if (consent === 'denied') return

    const payload: EventBase = {
      ...basePayload(),
      ...e,
      anon_id: this.anonId,
      session_id: this.sessId,
      user_id: this.userId ?? undefined
    }
    
    // Verifica se já existe evento idêntico na fila (dentro de 500ms)
    const now = Date.now()
    const isDuplicate = this.q.some(existing => {
      if (existing.event !== payload.event || existing.path !== payload.path) return false
      if (!existing.ts || !payload.ts) return false
      const timeDiff = Math.abs((payload.ts || now) - existing.ts)
      if (timeDiff > 500) return false // Apenas dentro de 500ms
      
      // Para eventos customizados, compara props
      if (payload.event !== 'page_view') {
        const existingProps = JSON.stringify(existing.props || {})
        const newProps = JSON.stringify(payload.props || {})
        return existingProps === newProps
      }
      // Para page_view, se tem mesmo path e timestamp próximo, é duplicado
      return true
    })
    
    if (isDuplicate) {
      return
    }
    
    this.q.push(payload)
    
    if (this.q.length >= (this.cfg.batchSize || 10)) this.flush()
    else this.armTimer()
  }

  private armTimer() {
    if (this.timer) return
    this.timer = setTimeout(() => {
      this.timer = null
      this.flush()
    }, this.cfg.flushIntervalMs) as unknown as number
  }

  async flush() {
    // Proteção contra flush simultâneo
    if (this.isFlushing) {
      return
    }

    if (this.q.length === 0) return
    
    // Cancela o timer se existir, pois vamos fazer flush agora
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    
    this.isFlushing = true
    
    try {
      // Pega todos os itens da fila e limpa
      const batch = this.q.splice(0, this.q.length)
      const body = { site_key: this.cfg.siteKey, batch }
      
      // Detecta país e adiciona ao header
      const country = detectCountry()
      
      // SEMPRE passa headers para forçar uso de fetch (sendBeacon não suporta headers)
      // Se país não foi detectado, passa objeto vazio mas ainda força fetch
      const headers: Record<string, string> = country ? { 'x-country': country } : {}
      
      await postJSON(this.cfg.endpoint, body, this.cfg.debug, headers)
    } finally {
      this.isFlushing = false
      
      // Se novos eventos foram adicionados enquanto estava fazendo flush, agenda novo flush
      if (this.q.length > 0) {
        this.armTimer()
      }
    }
  }
}
